import Vue from 'vue'
import Vuex from 'vuex'
import axios from '@/axios'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    isLogged: !!window.localStorage.getItem('user'),
    user: JSON.parse(window.localStorage.getItem('user')),
    flowGroups: [],
    flowLocales: {},
    flowEditorLocale: null,
    flowEdited: {},
    usersGroups: [],
    attachments: {},
    functions: [],
    version: null,
    isEdited: false,
    flowCopiedElement: null,
    flowCopiedBlock: null,
    flowBlockPasteTarget: null,
    openedContextMenuRef: null
  },
  mutations: {
    LOGIN_USER(state) {
      state.isLogged = true
      state.user = JSON.parse(window.localStorage.getItem('user'))
    },

    LOGOUT_USER(state) {
      state.isLogged = false
      state.user = null
    },

    UPDATE_ADMIN(state, admin) {
      state.user.admin = admin
      window.localStorage.setItem('user', JSON.stringify(state.user))
    },

    LOAD_FLOW(state, data) {
      state.flowGroups = data
    },

    LOAD_FLOW_LOCALES(state, data) {
      state.flowLocales = data
    },

    LOAD_USERS_GROUPS(state, data) {
      state.usersGroups = data
    },

    PUSH_NEW_FLOW_GROUP(state, data) {
      state.flowGroups.push(data)
    },

    PUSH_NEW_USERS_GROUP(state, data) {
      state.usersGroups.push(data)
    },

    UPDATE_GROUP_NAME(state, data) {
      for (let i = 0; i < state.flowGroups.length; i++) {
        if (state.flowGroups[i].id === data.id) {
          Vue.set(state.flowGroups[i], 'name', data.name)
          break
        }
      }
    },

    DELETE_GROUP(state, id) {
      for (let i = state.flowGroups.length - 1; i >= 0; i--) {
        if (state.flowGroups[i].id === id) {
          state.flowGroups.splice(i, 1)
          break
        }
      }
    },

    DELETE_USERS_GROUP(state, id) {
      for (let i = state.usersGroups.length - 1; i >= 0; i--) {
        if (state.usersGroups[i].id === id) {
          state.usersGroups.splice(i, 1)
          break
        }
      }
    },

    ADD_BLOCK_TO_GROUP(state, data) {
      for (let i = 0; i < state.flowGroups.length; i++) {
        if (state.flowGroups[i].id === data.group) {
          state.flowGroups[i].blocks.push(data.block)
          break
        }
      }
    },

    DELETE_BLOCK(state, block) {
      let done = false
      for (let i = 0; i < state.flowGroups.length; i++) {
        if (done) break
        if (state.flowGroups[i].id !== block.group_id) continue
        for (let o = 0; o < state.flowGroups[i].blocks.length; o++) {
          if (state.flowGroups[i].blocks[o].id === block.id) {
            state.flowGroups[i].blocks.splice(o, 1)
            done = true
            break
          }
        }
      }
    },

    PICK_FLOW_EDITOR_LOCALE(state, locale) {
      state.flowEditorLocale = locale
    },

    SET_FLOW_EDIT_FLAG(state, data) {
      if (state.flowEdited[data[0]] === data[1]) return
      Vue.set(state.flowEdited, data[0], data[1])
      state.flowEdited[data[0]] = data[1]
    },

    LOAD_ATTACHMENTS(state, attachments) {
      state.attachments = attachments
    },

    ADD_ATTACHMENT(state, attachment) {
      attachment.extension = attachment.url.match(/\.[a-zA-Z0-9]*$/)[0]
      Vue.set(state.attachments, attachment.uuid, attachment)
    },

    ATTACHMENTS_SYNC(state, attachments) {
      for (const attachment of attachments) {
        Vue.set(state.attachments[attachment.uuid], 'attachment_id', attachment.attachment_id)
        Vue.set(state.attachments[attachment.uuid], 'force_update', false)
      }
    },

    DELETE_ATTACHMENT(state, uuid) {
      Vue.delete(state.attachments, uuid)
    },

    ATTACHMENT_UPDATE(state, attachment) {
      attachment.extension = attachment.url.match(/\.[a-zA-Z0-9]*$/)[0]
      Vue.set(state.attachments, attachment.uuid, attachment)
    },

    LOAD_FUNCTIONS(state, functions) {
      state.functions = functions
    },

    ADD_LOCALE(state, locale) {
      Vue.set(state.flowLocales, locale.locale, locale)
    },

    DELETE_LOCALE(state, locale) {
      Vue.delete(state.flowLocales, locale)
    },

    LOAD_VERSION(state, version) {
      state.version = version
    },

    SET_EDITED(state, edited) {
      state.isEdited = edited
    },

    COPY_FLOW_ELEMENT(state, elementStr) {
      state.flowCopiedElement = elementStr
    },

    COPY_FLOW_BLOCK(state, blockStr) {
      state.flowCopiedBlock = blockStr
    },

    FLOW_BLOCK_PASTE_TARGET(state, id) {
      state.flowBlockPasteTarget = id
    },

    OPENED_CM_REF(state, ref) {
      state.openedContextMenuRef = ref
    },

    RENAME_USERS_GROUP(state, data) {
      for (const g of state.usersGroups) {
        if (g.id === data.id) {
          g.name = data.name
          break
        }
      }
    }
  },
  actions: {
    async loadFlowData(context, that) {
      try {
        const groupsReq = await axios.get('/api/flow/groups')
        const blocksReq = await axios.get('/api/flow/blocks')
        const blocks = {}
        const groups = []

        for (const block of blocksReq.data.blocks) {
          if (!blocks[block.group_id]) blocks[block.group_id] = []
          blocks[block.group_id].push(block)
        }

        for (const group of groupsReq.data.groups) {
          group.blocks = blocks[group.id] || []
          groups.push(group)
        }

        context.commit('LOAD_FLOW', groups)
      } catch (e) {
        that.notify('error', 'view.flow.error.load')
      }
    },

    async loadFlowLocales(context, that) {
      try {
        const localesReq = await axios.get('/api/flow/locales')
        const locales = {}

        for (const locale of localesReq.data.locales) {
          locales[locale.locale] = locale
        }
        context.commit('LOAD_FLOW_LOCALES', locales)
      } catch (e) {
        that.notify('error', 'view.flow.error.localeLoad')
      }
    },

    async loadUsersGroups(context) {
      const groups = await axios.get('/api/users/groups')
      context.commit('LOAD_USERS_GROUPS', groups.data.groups)
    },

    async loadAttachments(context) {
      try {
        const attachments = await axios.get('/api/attachments/list')
        const formed = {}
        for (const attachment of attachments.data.attachments) {
          attachment.extension = attachment.url.match(/\.[a-zA-Z0-9]*$/)[0]
          formed[attachment.uuid] = attachment
        }
        context.commit('LOAD_ATTACHMENTS', formed)
      } catch (e) {
        console.error(e)
      }
    },

    async loadFunctions(context) {
      try {
        const functionsReq = await axios.get('/api/system/functions')
        context.commit('LOAD_FUNCTIONS', functionsReq.data.functions)
      } catch (e) {
        console.error(e)
      }
    },

    async loadVersion(context) {
      try {
        const versionReq = await axios.get('/api/system/version')
        context.commit('LOAD_VERSION', versionReq.data.version)
      } catch (e) {
        console.error(e)
      }
    }
  },
  getters: {
    flowBlocks: state => {
      const o = {}
      for (const group in state.flowGroups) {
        for (const block of state.flowGroups[group].blocks) {
          o[block.uuid] = block
        }
      }
      return o
    },

    flowBlock: (state) => (uuid) => {
      let o = null
      for (const group in state.flowGroups) {
        if (o) break
        for (const block of state.flowGroups[group].blocks) {
          if (block.uuid !== uuid) continue
          o = block
        }
      }
      return o
    },

    defaultFlowLocale: state => {
      let o = null
      for (const locale in state.flowLocales) {
        if (!state.flowLocales[locale].default) continue
        o = state.flowLocales[locale]
        break
      }
      return o
    }
  }
})
